import _ from 'lodash';
import { createSelector } from 'reselect';

import { PpeType } from 'constants/company';
import {
  BorrowerType,
  HelocPurposeType,
  LoanStatus,
  PurposeOfChattel,
} from 'constants/loanApplication';
import { PAGE_CODES } from 'constants/pageCodes';
import {
  checkIsAdditionalLoanFlow,
  checkIsChattelLoanPurpose,
  checkIsLoanPurposeTypeBpl,
  checkIsLoanTypeFHA,
  checkIsPurchaseFlow,
  checkIsRefinanceFlow,
  getBorrowersOptions,
  getPersonFullName,
} from 'utils/loanApplication';

import { getMasterPermissionsToPage } from 'store/auth/selectors';

import { selectChattelDetails } from './chattelDetails.selectors';
import { selectLoanApplication } from './loanApplication.selectors';
import {
  selectIsLockedFromPricingStatus,
  selectPricingEngineTypeFromPricingStatus,
} from './pricingStatus.selectors';

export const selectRelatedLoans = _.memoize(
  createSelector(selectLoanApplication, ({ relatedLoans }) => relatedLoans ?? []),
);

export const selectIsLoanApplicationEditAllowed = createSelector(
  getMasterPermissionsToPage(PAGE_CODES.LOAN_APPLICATION),
  ({ isEditAllowed }) => isEditAllowed,
);

export const selectCommonInfoLoanApplication = createSelector(
  selectLoanApplication,
  ({ common }) => common,
);

export const selectLoanSubCompanyId = createSelector(
  selectCommonInfoLoanApplication,
  ({ subCompanyId } = {}) => subCompanyId ?? null,
);

export const selectIsEmptyCommonInfoLoanApplication = createSelector(
  selectCommonInfoLoanApplication,
  common => _.isEmpty(common),
);

// SUCCESS-only: a failed fetch keeps this false. LoanApplicationContent's validity guard (DEV-4226)
// depends on that — it redirects a non-sub-company user out of an editor whose data never arrived.
export const selectIsCommonInfoLoanApplicationFullyLoaded = createSelector(
  selectCommonInfoLoanApplication,
  ({ isLoanInfoLoaded, isLockInfoLoaded }) => !!isLoanInfoLoaded && !!isLockInfoLoaded,
);

// SETTLED (succeeded OR failed): "we know as much as we are ever going to". For consumers that must
// stop waiting once the requests are done, whatever the outcome — e.g. PathToSubmission, which would
// otherwise hold a skeleton forever on a single failed request. Deliberately distinct from
// ...FullyLoaded above: conflating the two would silently disable that redirect guard.
export const selectAreCommonInfoRequestsSettled = createSelector(
  selectCommonInfoLoanApplication,
  ({ isLoanInfoSettled, isLockInfoSettled }) => !!isLoanInfoSettled && !!isLockInfoSettled,
);

export const selectIsSavingLoanApplicationChanges = createSelector(
  selectCommonInfoLoanApplication,
  ({ isSavingLoanApplicationChanges }) => !!isSavingLoanApplicationChanges,
);

export const selectIsCancellingLoanApplicationChanges = createSelector(
  selectCommonInfoLoanApplication,
  ({ isCancellingLoanApplicationChanges }) => !!isCancellingLoanApplicationChanges,
);

export const selectIsLoanLocked = createSelector(
  selectCommonInfoLoanApplication,
  selectIsLockedFromPricingStatus,
  ({ lockDate }, isLockedFromPricingStatus) => !!lockDate || isLockedFromPricingStatus,
);

export const selectLoanPricingEngineType = createSelector(
  [selectCommonInfoLoanApplication, selectPricingEngineTypeFromPricingStatus],
  ({ pricingEngineType }, pricingEngineTypeFromPricingStatus) =>
    pricingEngineType || pricingEngineTypeFromPricingStatus,
);

export const selectIsLoanNoPpeEngineType = createSelector(
  selectLoanPricingEngineType,
  pricingEngineType => pricingEngineType === PpeType.NO_PPE,
);

export const selectIsLoanLenderEngineType = createSelector(
  selectLoanPricingEngineType,
  pricingEngineType => pricingEngineType === PpeType.LENDER_PPE,
);

export const selectIsOBEngineType = createSelector(
  selectLoanPricingEngineType,
  pricingEngineType => pricingEngineType === PpeType.OPTIMAL_BLUE_PPE,
);

export const selectIsOBUiEngineType = createSelector(
  selectLoanPricingEngineType,
  pricingEngineType => pricingEngineType === PpeType.OPTIMAL_BLUE_UI_PPE,
);

export const selectIsLoanOptimalBlueOrSifterEngineType = createSelector(
  selectLoanPricingEngineType,
  pricingEngineType =>
    pricingEngineType === PpeType.OPTIMAL_BLUE_PPE || pricingEngineType === PpeType.LOAN_SIFTER_PPE,
);

export const selectIsPurchaseLoanPurposeFromApplication = createSelector(
  selectCommonInfoLoanApplication,
  ({ loanPurpose }) => checkIsPurchaseFlow(loanPurpose),
);

export const selectIsRefinanceLoanPurposeFromApplication = createSelector(
  selectCommonInfoLoanApplication,
  ({ loanPurpose }) => checkIsRefinanceFlow(loanPurpose),
);

export const selectIsAdditionalLoanPurposeFromApplication = createSelector(
  selectCommonInfoLoanApplication,
  ({ loanPurpose }) => checkIsAdditionalLoanFlow(loanPurpose),
);

// Mirrors pricing's chattel gate (Application.LoanPurpose == ChattelLoan), so the fees form and the
// calculation agree on which loans derive escrow months from due dates (LEN-130).
export const selectIsChattelLoanPurposeFromApplication = createSelector(
  selectCommonInfoLoanApplication,
  common => checkIsChattelLoanPurpose(common?.loanPurpose),
);

// A chattel refinance pays off the home lien out of the loan (LEN-205), so its fees read as a refinance's.
export const selectIsRefinanceFeesFlow = createSelector(
  selectCommonInfoLoanApplication,
  selectChattelDetails,
  ({ loanPurpose, helocPurpose }, chattelDetails) => {
    const isHelocPurposeNotPurchase = helocPurpose !== HelocPurposeType.HOME_PURCHASE;
    const isChattelRefinance =
      checkIsChattelLoanPurpose(loanPurpose) &&
      chattelDetails?.purposeOfChattel === PurposeOfChattel.REFINANCE;

    return (
      checkIsRefinanceFlow(loanPurpose) ||
      (checkIsAdditionalLoanFlow(loanPurpose) && isHelocPurposeNotPurchase) ||
      isChattelRefinance
    );
  },
);

export const selectIsBplLoanPurposeTypeFromApplication = createSelector(
  selectCommonInfoLoanApplication,
  ({ loanPurposeType }) => checkIsLoanPurposeTypeBpl(loanPurposeType),
);

export const selectIsFHALoanType = createSelector(selectCommonInfoLoanApplication, ({ loanType }) =>
  checkIsLoanTypeFHA(loanType),
);

export const selectPrimaryBorrowerLastName = createSelector(
  selectCommonInfoLoanApplication,
  ({ primaryBorrowerLastName }) => primaryBorrowerLastName,
);

export const selectProgramAndPricingApplicationData = createSelector(
  selectCommonInfoLoanApplication,
  selectLoanApplication,
  ({ programId, productId, lockTerm, finalBuyPrice }, loanApplication) => ({
    programId,
    productId,
    lockTerm,
    finalBuyPrice,
    rate: _.get(loanApplication, [
      'propertyAndLoan',
      'loanAndLockEditData',
      'loan',
      'program',
      'rate',
    ]),
  }),
);

export const selectLoanHeaderApplicationData = createSelector(
  selectCommonInfoLoanApplication,
  ({ progressPercentage, milestoneProgress, statusDescription, percentDescription }) => ({
    progressPercentage,
    milestoneProgress,
    statusDescription,
    percentDescription,
  }),
);

export const selectLoanNumber = createSelector(
  selectCommonInfoLoanApplication,
  common => common?.loanNumber ?? null,
);

export const selectLoanMilestoneStatusName = createSelector(
  selectCommonInfoLoanApplication,
  ({ milestoneProgress, statusDescription } = {}) =>
    milestoneProgress?.name ?? statusDescription?.status ?? null,
);

// purposeOfChattel decides whether a chattel loan is rescindable (LEN-198); it is null until the
// chattel details are loaded or captured, which keeps the rescission default.
export const selectClosingDatesApplicationData = createSelector(
  selectCommonInfoLoanApplication,
  selectChattelDetails,
  ({ loanPurpose, programInternalName, helocPurpose, loanType }, chattelDetails) => ({
    application: {
      loanPurpose,
      helocPurpose,
      loanType,
      program: { internalName: programInternalName },
      purposeOfChattel: chattelDetails?.purposeOfChattel ?? null,
    },
  }),
);

export const selectIsLoanInitiatedWithErrors = createSelector(
  selectCommonInfoLoanApplication,
  ({ statusDescription } = {}) => statusDescription?.status === LoanStatus.DID_NOT_PREQUAL,
);

export const selectIsLoanCanceled = createSelector(
  selectCommonInfoLoanApplication,
  ({ statusDescription } = {}) => statusDescription?.status === LoanStatus.CANCELED,
);

export const selectIsLoanTBDBundleIssued = createSelector(
  selectCommonInfoLoanApplication,
  ({ statusDescription } = {}) => statusDescription?.status === LoanStatus.TBD_BUNDLE_ISSUED,
);

export const selectSuspenseReason = createSelector(
  selectCommonInfoLoanApplication,
  ({ suspenseReason, isLoanSuspended }) => (isLoanSuspended ? suspenseReason : ''),
);

const selectBorrowersValuesCommonInfo = createSelector(
  selectCommonInfoLoanApplication,
  ({ borrowers }) =>
    [...(borrowers || [])].sort(({ orderNumber }, { orderNumber: nextOrderNumber }) => {
      const equalOrLess = orderNumber === nextOrderNumber ? 0 : -1;

      return orderNumber > nextOrderNumber ? 1 : equalOrLess;
    }),
);

export const selectExtendedBorrowersCommonInfo = createSelector(
  selectBorrowersValuesCommonInfo,
  borrowers =>
    borrowers.map(borrower => ({
      ...borrower,
      borrowerId: borrower.id,
      fullName: getPersonFullName(borrower),
      fullNameWithMiddleName: getPersonFullName({ ...borrower, isWithMiddleName: true }),
    })),
);

export const selectBorrowerOptionsCommonInfo = createSelector(
  selectExtendedBorrowersCommonInfo,
  borrowers => getBorrowersOptions(borrowers),
);

export const selectPrimaryBorrowerCommonInfo = createSelector(
  selectBorrowersValuesCommonInfo,
  borrowers => borrowers.find(({ borrowerType }) => borrowerType === BorrowerType.PRIMARY),
);

export const selectPrimaryBorrowerIdCommonInfo = createSelector(
  selectPrimaryBorrowerCommonInfo,
  borrower => borrower?.id,
);

export const selectPrimaryBorrowerLatestCreditReportDocumentId = createSelector(
  selectPrimaryBorrowerCommonInfo,
  borrower => borrower?.latestCreditReportDocumentId,
);

export const selectBorrowersCommonInfoGroupsByRefNumber = createSelector(
  selectExtendedBorrowersCommonInfo,
  borrowers =>
    borrowers.reduce(
      (acc, borrower) => {
        const index = Number(!!borrower.latestCreditReportReferenceNumber);
        acc[index].push(borrower);

        return acc;
      },
      [[], []],
    ),
);

const selectNonBorrowerEntitiesValuesCommonInfo = createSelector(
  selectCommonInfoLoanApplication,
  ({ nonBorrowerEntities }) => nonBorrowerEntities || [],
);

export const selectExtendedNonBorrowerEntitiesCommonInfo = createSelector(
  selectNonBorrowerEntitiesValuesCommonInfo,
  nonBorrowerEntities =>
    nonBorrowerEntities.map(entity => ({
      ...entity,
      id: entity.nonBorrowerEntityId,
      fullName: getPersonFullName(entity),
      fullNameWithMiddleName: getPersonFullName({ ...entity, isWithMiddleName: true }),
    })),
);

export const selectNbesCommonInfoGroupsByRefNumber = createSelector(
  selectExtendedNonBorrowerEntitiesCommonInfo,
  nonBorrowerEntities =>
    nonBorrowerEntities.reduce(
      (acc, entity) => {
        const index = Number(!!entity.latestCreditReportReferenceNumber);
        acc[index].push(entity);

        return acc;
      },
      [[], []],
    ),
);

export const selectLoanChannelId = createSelector(
  selectCommonInfoLoanApplication,
  ({ channelId }) => channelId ?? null,
);
